import React, { createContext, useContext, useState, useEffect } from 'react';

const CurrencyContext = createContext();

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed USD prices for display
const USD_PRICES = {
  29: 19,    // Early Bird monthly
  34: 22,    // Standard monthly
  340: 220,  // Annual
  44.95: 29, // Keepsake Tin
  28.33: 18.33, // Annual monthly equivalent
  68: 44,    // Annual savings
  16: 10,    // Regular shipping
  20: 13,    // Express shipping
};

export function CurrencyProvider({ children }) {
  const [currency, setCurrency] = useState('AUD');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    detectCurrency();
  }, []);

  const detectCurrency = async () => {
    try {
      const cached = localStorage.getItem('bt_currency');
      if (cached) {
        setCurrency(cached);
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/detect-currency`);
      if (response.ok) {
        const data = await response.json();
        const detected = data.currency || 'AUD';
        setCurrency(detected);
        localStorage.setItem('bt_currency', detected);
      }
    } catch (error) {
      console.log('Currency detection failed, defaulting to AUD');
      setCurrency('AUD');
    } finally {
      setLoading(false);
    }
  };

  const toggleCurrency = () => {
    const next = currency === 'AUD' ? 'USD' : 'AUD';
    setCurrency(next);
    localStorage.setItem('bt_currency', next);
  };

  // Format price with fixed USD mapping
  const formatPrice = (priceInAUD, showDecimals = false) => {
    if (currency === 'USD') {
      const usdPrice = USD_PRICES[priceInAUD] !== undefined ? USD_PRICES[priceInAUD] : Math.round(priceInAUD * 0.65);
      const formatted = showDecimals ? usdPrice.toFixed(2) : Math.round(usdPrice);
      return `$${formatted}`;
    }
    const formatted = showDecimals ? priceInAUD.toFixed(2) : Math.round(priceInAUD);
    return `$${formatted}`;
  };

  const getCurrencyCode = () => currency;
  const getCurrencySymbol = () => '$';

  // Get the actual numeric price for calculations
  const getPrice = (priceInAUD) => {
    if (currency === 'USD') {
      return USD_PRICES[priceInAUD] !== undefined ? USD_PRICES[priceInAUD] : Math.round(priceInAUD * 0.65);
    }
    return priceInAUD;
  };

  const value = {
    currency,
    loading,
    formatPrice,
    getCurrencyCode,
    getCurrencySymbol,
    toggleCurrency,
    getPrice,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency() {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider');
  }
  return context;
}

export default CurrencyContext;
